import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, LoginCredentials } from '../types';
import { authService } from '../services/api/auth';
import { isTokenExpired, getTokenExpiresInMs } from '../utils/jwt';

// Refresh access token 2 minutes before expiry
const REFRESH_BUFFER_SECONDS = 120;

// Module-level state for proactive refresh timer and deduplication
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let refreshPromise: Promise<boolean> | null = null;

function clearRefreshTimer() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function scheduleTokenRefresh(accessToken: string) {
  clearRefreshTimer();

  const expiresInMs = getTokenExpiresInMs(accessToken);
  if (expiresInMs === null || expiresInMs <= 0) return;

  // Refresh 2 min before expiry, but at least 10s from now
  const refreshInMs = Math.max(expiresInMs - REFRESH_BUFFER_SECONDS * 1000, 10_000);

  refreshTimer = setTimeout(() => {
    useAuthStore.getState().refreshTokens();
  }, refreshInMs);
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  _hasHydrated: boolean;
}

interface AuthActions {
  login: (credentials: LoginCredentials) => Promise<boolean>;
  logout: () => Promise<void>;
  initializeAuth: () => Promise<void>;
  refreshTokens: () => Promise<boolean>;
  clearError: () => void;
  setHasHydrated: (state: boolean) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      _hasHydrated: false,

      // Actions
      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authService.login(credentials);

          set({
            user: response.user,
            accessToken: response.tokens.accessToken,
            refreshToken: response.tokens.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          scheduleTokenRefresh(response.tokens.accessToken);

          return true;
        } catch (error) {
          const message =
            error instanceof Error ? error.message : 'Login failed';
          set({
            isLoading: false,
            error: message,
            isAuthenticated: false,
          });

          return false;
        }
      },

      logout: async () => {
        clearRefreshTimer();
        const { refreshToken } = get();

        try {
          if (refreshToken) {
            await authService.logout(refreshToken);
          }
        } catch {
          // Ignore logout errors - still clear local state
        }

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
        });
      },

      initializeAuth: async () => {
        const { accessToken, refreshToken } = get();

        // No tokens — not authenticated
        if (!accessToken || !refreshToken) {
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            _hasHydrated: true,
          });
          return;
        }

        // Access token still valid — schedule proactive refresh
        if (!isTokenExpired(accessToken, REFRESH_BUFFER_SECONDS)) {
          scheduleTokenRefresh(accessToken);
          set({ _hasHydrated: true });
          return;
        }

        // Access token expired or about to expire — attempt refresh
        if (!isTokenExpired(refreshToken)) {
          const refreshed = await get().refreshTokens();
          if (refreshed) {
            set({ _hasHydrated: true });
            return;
          }
        }

        // Refresh failed or refresh token expired — clear auth
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          _hasHydrated: true,
        });
      },

      refreshTokens: async () => {
        // Deduplicate concurrent refresh calls — return existing promise
        if (refreshPromise) return refreshPromise;

        const { refreshToken } = get();
        if (!refreshToken || isTokenExpired(refreshToken)) {
          return false;
        }

        refreshPromise = (async () => {
          try {
            const response = await authService.refreshToken(refreshToken);

            set({
              accessToken: response.tokens.accessToken,
              refreshToken: response.tokens.refreshToken,
            });

            scheduleTokenRefresh(response.tokens.accessToken);

            return true;
          } catch {
            return false;
          } finally {
            refreshPromise = null;
          }
        })();

        return refreshPromise;
      },

      setTokens: (accessToken: string, refreshToken: string) => {
        set({ accessToken, refreshToken });
      },

      clearError: () => set({ error: null }),

      setHasHydrated: (state: boolean) => set({ _hasHydrated: state }),
    }),
    {
      name: 'safeload-auth-storage',
      partialize: state => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => state => {
        // Validate tokens and schedule refresh before marking as ready
        state?.initializeAuth();
      },
    }
  )
);
